import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { routes } from '../../../../utils/helpers/ApiClient';
import {
  ClearedExternalLink,
  ClearedLink,
  IconButton,
} from '../../../common/controls/Controls';
import MobileMenu from './MobileMenu/MobileMenu';
import { SHOW_MOBILE_NAV_WIDTH } from '../../../../utils/constants/enums.js';
import { useTranslation } from 'react-i18next';
import { MenuFoldOutlined, MenuOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { changeMobileSwipe } from '../../../../redux/slices/coreSlice';
import { useGetMenuTreeQuery } from '../../../../redux/services/menu';

export const getTitleOfItem = (item, isEnglish) => {
  if (isEnglish) {
    return item.text_en;
  }
  return item.text_sk;
};

/**
 * Ensures a path starts with a forward slash, or returns '/' if the path is empty
 * @param {string} path - The path to check
 * @returns {string} Path with slash
 */
const ensureLeadingSlash = (path) => {
  if (!path) {
    return '/';
  }

  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Replaces one route prefix with another in a given path
 * Used for converting between language-specific routes (e.g., '/clanky/' to '/articles/')
 * @param {string} path - The original path
 * @param {string} fromRoute - The old route prefix to replace
 * @param {string} toRoute - The new route prefix to use
 * @returns {string} Path with replaced route prefix
 */
const replaceRoutePrefix = (path, fromRoute, toRoute) => {
  if (path === fromRoute) {
    return toRoute;
  }

  if (path.startsWith(`${fromRoute}/`)) {
    return `${toRoute}${path.slice(fromRoute.length)}`;
  }

  return path;
};

/**
 * Generates the correct URL for a menu item based on language and item type
 * Handles three cases: articles with language-specific slugs, external links, and internal routes
 * For missing language variants, it automatically translates route prefixes between languages
 * @param {Object} item - Menu item object with article
 * @param {boolean} isEnglish - Whether the current language is English
 * @returns {string} The URL to navigate to for this menu item
 */
export const getMenuItemUrl = (item, isEnglish) => {
  const { article } = item;

  // If menu item has an associated article, generate the article page URL
  if (article) {
    const articleSlug = (isEnglish ? article.slug_en : article.slug_sk) || '-';
    const articleRoute = isEnglish ? routes.articleEnglish : routes.article;

    return `${articleRoute}/${articleSlug}`;
  }

  // For external links, return the destination URL directly
  if (item.item_type !== 'internal') {
    return item.destination;
  }

  // For internal routes, select the appropriate language variant destination
  let internalUrl = isEnglish
    ? item.destination_en || item.destination
    : item.destination || item.destination_en;

  internalUrl = ensureLeadingSlash(internalUrl);

  // If English variant doesn't exist, translate Slovak route prefixes to English
  if (isEnglish && !item.destination_en) {
    internalUrl = replaceRoutePrefix(
      internalUrl,
      routes.article,
      routes.articleEnglish,
    );
    internalUrl = replaceRoutePrefix(
      internalUrl,
      routes.department,
      routes.departmentEnglish,
    );
  }

  // If Slovak variant doesn't exist, translate English route prefixes to Slovak
  if (!isEnglish && !item.destination) {
    internalUrl = replaceRoutePrefix(
      internalUrl,
      routes.articleEnglish,
      routes.article,
    );
    internalUrl = replaceRoutePrefix(
      internalUrl,
      routes.departmentEnglish,
      routes.department,
    );
  }

  return internalUrl;
};

const MainNavigation = () => {
  const [topNavHide, setTopNavHide] = useState(undefined);
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [hideMainNav, setHideMainNav] = useState(false);
  const [keyboardOpenItemId, setKeyboardOpenItemId] = useState(null);
  const [keyboardOpenPosition, setKeyboardOpenPosition] = useState('first');
  const menubarRef = useRef(null);
  const submenuRefs = useRef({});
  const shouldFocusSubmenuRef = useRef(false);

  const { i18n } = useTranslation();

  const isEnglish = i18n.language === 'en';
  const topNavigationHideClass = topNavHide ? 'hide' : '';
  const showMobileNav = window.innerWidth <= SHOW_MOBILE_NAV_WIDTH;

  const dispatch = useDispatch();
  const isMobileSwipeOpened = useSelector(
    (state) => state.core.isMobileSwipeOpened,
  );
  const { t } = useTranslation();

  const { data: menu } = useGetMenuTreeQuery(isEnglish);

  useEffect(() => {
    const handleScrolling = () => {
      setTopNavHide(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScrolling);

    return () => {
      window.removeEventListener('scroll', handleScrolling);
    };
  }, []);

  const isSpecialDomain =
    typeof window !== 'undefined' && window.location.hostname === 'localhost';

  const renderLogo = (
    <Col
      xs={{ span: 12 }}
      span={10}
      md={{ span: 8 }}
      lg={{ span: 8 }}
      xl={{ span: 8 }}
      xxl={{ span: 5 }}
      className={`navigation-logo-container`}
    >
      <ClearedLink
        to="/"
        title={t('Vytvorené študentmi FRI UNIZA')}
        style={{ width: '150%' }}
      >
        <img
          className="img-responsive"
          src={`${isEnglish ? '/images/FRI_en_blue.svg' : '/images/FRI_sk_blue.svg'}`}
          alt={t('Fakulta riadenia a informatiky - Žilinská univerzita')}
        />
      </ClearedLink>
    </Col>
  );

  const getCommonNavElement = (item, linkProps = {}) => {
    if (item.item_type === 'external') {
      return (
        <ClearedExternalLink href={`${item.destination}`} {...linkProps}>
          {getTitleOfItem(item, isEnglish)}
          <i className="fas fa-external-link-alt" />
        </ClearedExternalLink>
      );
    }

    const url = getMenuItemUrl(item, isEnglish);

    return (
      <ClearedLink to={url || '/'} {...linkProps}>
        {getTitleOfItem(item, isEnglish)}
      </ClearedLink>
    );
  };

  const getTopLevelMenuItems = () => {
    if (!menubarRef.current) {
      return [];
    }

    return Array.from(
      menubarRef.current.querySelectorAll('[data-main-nav-top-level="true"]'),
    );
  };

  const focusTopLevelMenuItemByIndex = (index) => {
    const topLevelMenuItems = getTopLevelMenuItems();

    if (topLevelMenuItems.length === 0) {
      return;
    }

    const normalizedIndex =
      (index + topLevelMenuItems.length) % topLevelMenuItems.length;

    topLevelMenuItems[normalizedIndex].focus();
  };

  const getSubmenuItems = (itemId) => {
    const submenuElement = submenuRefs.current[itemId];

    if (!submenuElement) {
      return [];
    }

    return Array.from(
      submenuElement.querySelectorAll('[role="menuitem"]:not([tabindex="-1"])'),
    );
  };

  useEffect(() => {
    if (!keyboardOpenItemId || hideMainNav || !shouldFocusSubmenuRef.current) {
      return;
    }

    shouldFocusSubmenuRef.current = false;
    focusSubmenuItem(keyboardOpenItemId, keyboardOpenPosition);
  }, [hideMainNav, keyboardOpenItemId, keyboardOpenPosition]);

  /**
   * Moves focus horizontally between submenu columns while preserving row position.
   * If the target column has fewer items, focus clamps to the nearest available row.
   * @param {string|number} itemId
   * @param {1|-1} direction
   * @returns {boolean}
   */
  const moveFocusAcrossSubmenuColumns = (itemId, direction) => {
    const submenuElement = submenuRefs.current[itemId];

    if (!submenuElement) {
      return false;
    }

    const activeElement = document.activeElement;

    if (!(activeElement instanceof HTMLElement)) {
      return false;
    }

    /* Each second-level wrapper acts as one visual column inside the open submenu. */
    const columns = Array.from(
      submenuElement.querySelectorAll('.second-level-menu-wrapper'),
    );

    if (columns.length < 2) {
      return false;
    }

    const currentColumn = activeElement.closest('.second-level-menu-wrapper');

    if (!(currentColumn instanceof HTMLElement)) {
      return false;
    }

    const currentColumnIndex = columns.findIndex(
      (column) => column === currentColumn,
    );

    if (currentColumnIndex === -1) {
      return false;
    }

    /* Find the current row so we can keep the same vertical position when moving sideways. */
    const currentColumnItems = Array.from(
      currentColumn.querySelectorAll('[role="menuitem"]:not([tabindex="-1"])'),
    );
    const currentRowIndex = currentColumnItems.findIndex(
      (submenuItem) => submenuItem === activeElement,
    );

    let targetColumnIndex = currentColumnIndex + direction;

    /* Walk toward the requested side and skip empty columns until we find one with items. */
    while (targetColumnIndex >= 0 && targetColumnIndex < columns.length) {
      const targetColumn = columns[targetColumnIndex];
      const targetColumnItems = Array.from(
        targetColumn.querySelectorAll('[role="menuitem"]:not([tabindex="-1"])'),
      );

      if (targetColumnItems.length > 0) {
        /* Keep the same row when possible; otherwise clamp to the last available item. */
        const targetRowIndex = Math.max(
          0,
          Math.min(currentRowIndex, targetColumnItems.length - 1),
        );

        targetColumnItems[targetRowIndex].focus();
        return true;
      }

      targetColumnIndex += direction;
    }

    return false;
  };

  const focusSubmenuItem = (itemId, position = 'first') => {
    requestAnimationFrame(() => {
      const submenuItems = getSubmenuItems(itemId);

      if (submenuItems.length === 0) {
        return;
      }

      const targetSubmenuItem =
        position === 'last'
          ? submenuItems[submenuItems.length - 1]
          : submenuItems[0];

      if (targetSubmenuItem instanceof HTMLElement) {
        targetSubmenuItem.focus();
      }
    });
  };

  const closeKeyboardSubmenu = () => {
    setKeyboardOpenItemId(null);
    setKeyboardOpenPosition('first');
    setHideMainNav(true);
  };

  const openKeyboardSubmenu = (itemId, position = 'first') => {
    setHideMainNav(false);
    shouldFocusSubmenuRef.current = true;
    setKeyboardOpenItemId(itemId);
    setKeyboardOpenPosition(position);
  };

  /**
   * Handles first level menu items keyboard actions
   * @param {React.KeyboardEvent<HTMLButtonElement>} event
   * @param {string} itemId
   * @param {boolean} hasChildren
   * @param {number} itemIndex
   */
  const handleFirstLevelTriggerKeyDown = (
    event,
    itemId,
    hasChildren,
    itemIndex,
  ) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeKeyboardSubmenu();
      focusTopLevelMenuItemByIndex(itemIndex);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      closeKeyboardSubmenu();
      focusTopLevelMenuItemByIndex(itemIndex + 1);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      closeKeyboardSubmenu();
      focusTopLevelMenuItemByIndex(itemIndex - 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      closeKeyboardSubmenu();
      focusTopLevelMenuItemByIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      closeKeyboardSubmenu();
      focusTopLevelMenuItemByIndex((menu?.length || 1) - 1);
      return;
    }

    if (!hasChildren) {
      return;
    }

    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'ArrowDown'
    ) {
      event.preventDefault();
      openKeyboardSubmenu(itemId, 'first');
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      openKeyboardSubmenu(itemId, 'last');
    }
  };

  const handleSubmenuKeyDown = (event, itemId, itemIndex) => {
    const submenuItems = getSubmenuItems(itemId);
    const currentItemIndex = submenuItems.findIndex(
      (submenuItem) => submenuItem === document.activeElement,
    );

    if (event.key === 'Tab') {
      closeKeyboardSubmenu();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeKeyboardSubmenu();
      focusTopLevelMenuItemByIndex(itemIndex);
      return;
    }

    if (event.key === 'ArrowDown' && submenuItems.length > 0) {
      event.preventDefault();
      const nextIndex =
        currentItemIndex === -1
          ? 0
          : (currentItemIndex + 1) % submenuItems.length;
      submenuItems[nextIndex].focus();
      return;
    }

    if (event.key === 'ArrowUp' && submenuItems.length > 0) {
      event.preventDefault();
      const previousIndex =
        currentItemIndex === -1
          ? submenuItems.length - 1
          : (currentItemIndex - 1 + submenuItems.length) % submenuItems.length;
      submenuItems[previousIndex].focus();
      return;
    }

    if (event.key === 'Home' && submenuItems.length > 0) {
      event.preventDefault();
      submenuItems[0].focus();
      return;
    }

    if (event.key === 'End' && submenuItems.length > 0) {
      event.preventDefault();
      submenuItems[submenuItems.length - 1].focus();
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();

      const direction = event.key === 'ArrowRight' ? 1 : -1;
      moveFocusAcrossSubmenuColumns(itemId, direction);
    }
  };

  /**
   * Handles opening of first level menu items on click
   * @param {React.MouseEvent<HTMLButtonElement>} event
   * @param {string} itemId
   * @param {boolean} hasChildren
   */
  const handleFirstLevelTriggerClick = (event, itemId, hasChildren) => {
    if (!hasChildren) {
      return;
    }

    if (event.detail === 0) {
      return;
    }

    shouldFocusSubmenuRef.current = false;
    setHideMainNav(false);
    setKeyboardOpenItemId((previousItemId) =>
      previousItemId === itemId ? null : itemId,
    );
  };

  const renderMainMenu = (
    <Col
      lg={{ span: 14 }}
      xl={{ span: 13 }}
      xxl={{ span: 11 }}
      style={{ position: 'static' }}
    >
      <ul
        className="big-menu-navigation"
        role="menubar"
        aria-orientation="horizontal"
        aria-label={t('Hlavné menu')}
        ref={menubarRef}
      >
        {menu &&
          menu.map((item, index) => {
            const hasChildren = item.children && item.children.length > 0;
            const isKeyboardOpen =
              keyboardOpenItemId === item.id && !hideMainNav;
            const submenuId = `main-nav-submenu-${item.id}`;
            const triggerId = `main-nav-trigger-${item.id}`;
            const submenuLinkProps = {
              role: 'menuitem',
              tabIndex: isKeyboardOpen ? 0 : -1,
            };

            return (
              <li
                onMouseEnter={() => {
                  setHideMainNav(false);
                  setKeyboardOpenItemId(null);
                }}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setKeyboardOpenItemId(null);
                  }
                }}
                onKeyDown={(event) => {
                  const isTopLevelFocusTarget =
                    event.target instanceof HTMLElement &&
                    event.target.dataset.mainNavTopLevel === 'true';

                  if (!isTopLevelFocusTarget) {
                    return;
                  }

                  handleFirstLevelTriggerKeyDown(
                    event,
                    item.id,
                    hasChildren,
                    index,
                  );
                }}
                key={item.id}
                className={`first-level ${isKeyboardOpen ? 'is-open' : ''}`}
                role="none"
              >
                {hasChildren ? (
                  <button
                    id={triggerId}
                    type="button"
                    className="first-level-link"
                    role="menuitem"
                    data-main-nav-top-level="true"
                    aria-haspopup="menu"
                    aria-expanded={isKeyboardOpen}
                    aria-controls={submenuId}
                    onClick={(event) =>
                      handleFirstLevelTriggerClick(event, item.id, hasChildren)
                    }
                  >
                    {getTitleOfItem(item, isEnglish)}
                    <i className="fas fa-angle-down" />
                  </button>
                ) : (
                  getCommonNavElement(item, {
                    role: 'menuitem',
                    'data-main-nav-top-level': 'true',
                  })
                )}
                {hasChildren && (
                  <div
                    id={submenuId}
                    role="menu"
                    aria-labelledby={triggerId}
                    aria-orientation="vertical"
                    ref={(element) => {
                      if (element) {
                        submenuRefs.current[item.id] = element;
                        return;
                      }

                      delete submenuRefs.current[item.id];
                    }}
                    onClick={() => {
                      setHideMainNav(true);
                      setKeyboardOpenItemId(null);
                    }}
                    onKeyDown={(event) =>
                      handleSubmenuKeyDown(event, item.id, index)
                    }
                    className={`menu-content-wrapper ${
                      (hideMainNav && 'hidden') || ''
                    }`}
                    style={{
                      overflow: 'hidden',
                    }}
                  >
                    <div className="menu-content">
                      {item.children.map((secondLevel) => (
                        <div
                          key={secondLevel.id}
                          className="second-level-menu-wrapper"
                          style={{
                            width: `${100 / item.children.length}%`,
                            overflow: 'hidden',
                          }}
                        >
                          <div className={'second-level-title'}>
                            {getTitleOfItem(secondLevel, isEnglish)}
                          </div>

                          <div className={'second-level-items-wrapper'}>
                            {secondLevel.children.map((thirdLevelItem) => (
                              <div
                                key={thirdLevelItem.id}
                                className={'third-level-menu-item'}
                              >
                                {(thirdLevelItem.children &&
                                  thirdLevelItem.children.length > 0 && (
                                    <>
                                      <div className={'title'}>
                                        {getTitleOfItem(
                                          thirdLevelItem,
                                          isEnglish,
                                        )}
                                      </div>
                                      <div className="fourth-level-items-wrapper">
                                        {thirdLevelItem.children.map(
                                          (fourthLevelItem) => (
                                            <div
                                              key={fourthLevelItem.id}
                                              className="fourth-level-menu-item"
                                            >
                                              {getCommonNavElement(
                                                fourthLevelItem,
                                                submenuLinkProps,
                                              )}
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </>
                                  )) ||
                                  getCommonNavElement(
                                    thirdLevelItem,
                                    submenuLinkProps,
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
      </ul>
    </Col>
  );

  const renderMobileButton = (
    <Col
      xs={{ span: 12 }}
      span={9}
      md={{ span: 11 }}
      style={{ display: 'flex', justifyContent: 'flex-end' }}
    >
      <div className="mobile-menu-button-wrapper">
        {/* //Main menu mobile icon */}
        <IconButton
          icon={<MenuOutlined />}
          tooltip={t('Hlavné menu')}
          aria-label={t('Hlavné menu')}
          aria-haspopup="menu"
          aria-expanded={mobileMenuOpened}
          aria-controls="main-mobile-navigation"
          onClick={() => setMobileMenuOpened(!mobileMenuOpened)}
        />
        {/* //Side banner mobile icon */}
        <IconButton
          icon={<MenuFoldOutlined />}
          tooltip={t('Bočný panel')}
          aria-label={t('Bočný panel')}
          onClick={() => dispatch(changeMobileSwipe(!isMobileSwipeOpened))}
        />
      </div>
    </Col>
  );

  return (
    <>
      <div
        className={`main-navigation-container ${topNavigationHideClass}`}
        style={{ backgroundColor: isSpecialDomain ? 'Tomato' : undefined }}
      >
        <Row align="center">
          {renderLogo}
          {!showMobileNav && renderMainMenu}
          {renderMobileButton}
        </Row>
      </div>
      {showMobileNav && mobileMenuOpened && (
        <MobileMenu
          closeMenu={() => setMobileMenuOpened(false)}
          menu={menu}
          withHideClass={topNavHide}
        />
      )}
    </>
  );
};

export default MainNavigation;
